import {
  Tab as MuiTab,
  TabPanel as MuiTabPanel,
  Tabs as MuiTabs,
  TabsList as MuiTabsList,
  TabPanelProps,
  TabProps,
  TabsListProps,
  TabsProps,
} from '@mui/base';
import { Anchor } from 'components/ui';
import _ from 'lodash';
import { useRouter } from 'next/router';
import { createContext, useContext, useEffect, useState } from 'react';

interface ITabsContext {
  activeTabValue: string;
  isInternallyControlled: boolean;
  setActiveTabValue: (value: string) => void;
}

const TabsContext = createContext<ITabsContext>({
  activeTabValue: undefined,
  isInternallyControlled: true,
  setActiveTabValue: _.noop,
});

export const useTabsContext = () => useContext(TabsContext);

/**
 * This is the Tabs component system that is used to create a tabbed navigation.
 * It is a wrapper around the MUI Tabs component and should be used for
 * page-level navigation only. Use MUI directly if attempting to create a tabbed
 * component within a page.
 *
 * Do not apply styles to these components directly.
 */

export interface ITabsProps extends TabsProps {
  defaultValue?: string;
  value?: string;
}

export interface ITabsListProps extends TabsListProps {}

export interface ITabProps extends Omit<TabProps<typeof Anchor>, 'className'> {
  className?: string | ((props: { isActive: boolean }) => string);
  isActive?: boolean;
  value: string;
}

export interface ITabPanelProps extends TabPanelProps {
  value: string;
}

const Tab = ({ children, className, value, ...rest }: ITabProps) => {
  const { activeTabValue, isInternallyControlled, setActiveTabValue } =
    useTabsContext();
  const { asPath } = useRouter();

  const hasMatchingPath = _.includes(asPath, rest.href);
  const isActive = activeTabValue === value;

  /**
   * If internally controlled, set the active tab value based on the path. This
   * is useful for deep linking into a page.
   */
  if (isInternallyControlled && hasMatchingPath && !isActive) {
    setActiveTabValue(value);
  }

  return (
    <MuiTab<typeof Anchor>
      className={_.isFunction(className) ? className({ isActive }) : className}
      slots={{ root: Anchor }}
      {...rest}
    >
      {children}
    </MuiTab>
  );
};

const Tabs = ({ defaultValue, value, ...rest }: ITabsProps) => {
  /**
   * If the `value` prop is provided, the `Tabs` component and its active tab
   * state is externally controlled.
   */
  const isInternallyControlled = _.isUndefined(value);
  const initialActiveTabValue = isInternallyControlled ? defaultValue : value;

  const [activeTabValue, setActiveTabValue] = useState(initialActiveTabValue);

  useEffect(() => {
    if (!isInternallyControlled) {
      setActiveTabValue(value);
    }
  }, [value]);

  return (
    <TabsContext.Provider
      value={{
        activeTabValue,
        isInternallyControlled,
        setActiveTabValue,
      }}
    >
      <MuiTabs value={activeTabValue} {...rest} />
    </TabsContext.Provider>
  );
};

const TabPanel = (props: ITabPanelProps) => <MuiTabPanel {...props} />;

Tabs.Tab = Tab;
Tabs.TabPanel = TabPanel;
Tabs.TabsList = MuiTabsList;

export { Tabs };
